import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline'
import { useAuth } from '@/contexts/AuthContext'
import { hotelsApi } from '@/services/api'
import {
  evaluateHotelContactCompleteness,
  type HotelContactSource,
} from '@/utils/hotelContactCompleteness'

/**
 * #1443 — sticky top warning shown ONLY to hotel users whose Affiliation Contact
 * or Finance Contact is incomplete. Mirrors AffiliateCompanyBankBanner in
 * placement, scope and shape, so the two read as one mechanism.
 *
 * Scope notes, matching the affiliate banner deliberately:
 *  - Hotels ONLY. An admin session never sees this, even one that happens to own
 *    a hotel: an admin reviewing someone else's profile must not be warned about
 *    their own. Under impersonation the session IS the hotel user, so the banner
 *    evaluates that hotel's real data.
 *  - Affiliate users have no hotel association, so the hotelId gate keeps this
 *    off every affiliate page.
 *  - The completeness rules live in the shared helper, never inline here, so this
 *    banner and the hotel profile page cannot drift apart (#1321).
 *  - The query key IS HotelDetailsPage's hotel read (`['hotel-details', id]`),
 *    so React Query serves a single shared fetch and the banner clears the
 *    moment a contact save on that page invalidates it. #1699: this used to be
 *    `['hotel', id]`, which no save on /hotel-details touches, so the banner
 *    lingered until its read went stale. Both queryFns return the same
 *    `getHotel(id).data` payload, so sharing the cache entry is safe.
 */
export const HotelProfileContactsBanner = () => {
  const { t } = useTranslation('hotels')
  const navigate = useNavigate()
  const { user } = useAuth()

  const hotelId = user?.hotels?.find((h) => h.isActive)?.hotelId

  const isAdminSession =
    !!user?.roles?.includes('admin') ||
    !!user?.roles?.includes('global_admin') ||
    user?.userType === 'admin' ||
    user?.userType === 'global_admin'

  const enabled = !!hotelId && !isAdminSession

  const { data: hotel } = useQuery({
    queryKey: ['hotel-details', hotelId],
    queryFn: async () => {
      const res = await hotelsApi.getHotel(hotelId as string)
      // The shared `Hotel` type does not declare contact_info even though the
      // endpoint returns it — HotelDetailsPage works around this with `as any`.
      // Narrow to just the shape this banner reads instead, so the cast is one
      // documented line at the boundary rather than an untyped value in flight.
      return (res.data ?? null) as HotelContactSource | null
    },
    enabled,
  })

  if (!enabled) return null
  // Wait for the read to resolve so the banner never flashes on load.
  if (hotel === undefined) return null

  const { isIncomplete } = evaluateHotelContactCompleteness(hotel ?? {})
  if (!isIncomplete) return null

  return (
    <div role="alert" className="bg-red-50 border-b-2 border-red-300 px-4 py-2.5 text-red-800">
      <div className="container mx-auto flex items-center justify-center gap-3 text-center">
        <ExclamationTriangleIcon className="h-5 w-5 flex-shrink-0 text-red-600" aria-hidden="true" />
        <span className="text-sm font-medium">
          {t(
            'contactsWarning.message',
            'Your hotel profile is incomplete. Add your Affiliation Contact and Finance Contact to finish setting up your account.',
          )}{' '}
          <button
            type="button"
            // #1568 — the hotel user's own profile page, opened on this banner's
            // hotel and scrolled to the contacts block. /hotels/:id is admin-only.
            onClick={() => navigate(`/hotel-details?hotelId=${hotelId}#profile-contacts`)}
            className="font-bold underline text-red-700 hover:text-red-900"
          >
            {t('contactsWarning.cta', 'Complete now')}
          </button>
        </span>
      </div>
    </div>
  )
}
